import ExecutionEnvironment from 'exenv';

import {
  CART_CHANGE_FAILURE,
  CART_ERROR,
  CART_IS_LOADING,
  CART_RESET_STATE,
  PIN_IS_VALID,
  RECEIVE_CART_ITEMS,
  RECEIVE_CART_PROMOS,
  RECEIVE_PIN,
  RECEIVE_TRACKING_NUMBER,
  REQUEST_CART_UPDATE,
  SHOW_CART_MODAL,
  UPDATE_CART_COUNT
} from 'constants/reduxActions';
import { HYDRA_6PM_HARDROUTE, HYDRA_RET } from 'constants/hydraTests';
import {
  cartUpdatedEvent,
  navigateToCheckoutEvent
} from 'events/cart';

import {
  STORE_REWARDS_TRANSPARENCY_POINTS_FOR_CART
} from 'store/ducks/rewards/types';
import { trackEvent } from 'helpers/analytics';
import { checkCustomerAuthentication } from 'actions/authentication';
import { isAssigned, isInAssignment, triggerAssignment } from 'actions/ab';
import { firePixelServer } from 'actions/pixelServer';
import { err, setError } from 'actions/errors';
import { setSessionCookies } from 'actions/session';
import { fetchProductRecos } from 'actions/recos';
import { cartData, changeCart, getLandingPageInfo, getPin, getTrackingLabel, validatePin } from 'apis/mafia';
import { fetchErrorMiddleware, fetchErrorMiddlewareAllowedErrors } from 'middleware/fetchErrorMiddleware';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import marketplace from 'cfg/marketplace.json';
const { checkout: { checkoutUrl, placeboHydraTest } } = marketplace;

const CART_RECO_COUNT = 6;

function receiveCartItems(cart) {
  return {
    type: RECEIVE_CART_ITEMS,
    cartCount: cart.activeItemTotalQuantity || 0,
    cartObj: cart
  };
}

export function resetCartState() {
  return {
    type: CART_RESET_STATE
  };
}

export function changeCartCount(cartCount) {
  return {
    type: UPDATE_CART_COUNT,
    cartCount
  };
}

export function requestCartUpdate() {
  return {
    type: REQUEST_CART_UPDATE
  };
}

export function cartChangeFailure(errorDetail) {
  return {
    type: CART_CHANGE_FAILURE,
    errorDetail
  };
}

export function cartError(error) {
  return {
    type: CART_ERROR,
    error
  };
}

export function cartIsLoading() {
  return {
    type: CART_IS_LOADING
  };
}

export function showCartModal(isShowing, stockId) {
  return {
    type: SHOW_CART_MODAL,
    isShowing,
    stockId
  };
}

export function receivePin(pin) {
  return {
    type: RECEIVE_PIN,
    pin
  };
}

export function pinIsValid(isValid) {
  return {
    type: PIN_IS_VALID,
    isValid
  };
}

export function receiveTrackingNumber(trackingNumber) {
  return {
    type: RECEIVE_TRACKING_NUMBER,
    trackingNumber
  };
}

export function receiveCartPromos(promos) {
  return {
    type: RECEIVE_CART_PROMOS,
    promos
  };
}

export function receiveCartRewards(transparencyPointsForCart, isEnrolled) {
  return {
    type: STORE_REWARDS_TRANSPARENCY_POINTS_FOR_CART,
    transparencyPointsForCart,
    isEnrolled
  };
}

function fireCartPixelServer(cart) {
  const { activeItems } = cart;
  const items = [];
  for (let i = 0; i < activeItems.length; i++) {
    const item = activeItems[i];
    items.push({
      'asin': item.parentAsin,
      'childAsin': item.asin,
      'productId': item.productId,
      'childStockId': item.stockId,
      'name': item.productName,
      'qty': item.quantity,
      'price': item.price,
      'brandName': item.brandName,
      'type': item.glProductGroupType,
      'styleId': item.styleId
    });
  }

  return firePixelServer('cart', {
    cart: {
      'itemCount': cart.activeItemTotalQuantity,
      'subtotal': cart.subtotal.amount,
      'grandTotal': cart.subtotalWithTaxAndDiscounts.amount,
      'tax': (cart.subtotalWithTax.amount - cart.subtotal.amount),
      'items': items
    }
  });
}

export function updateCartCount({ activeItemTotalQuantity: count }, doc = document, EventConstructor = CustomEvent) {
  doc.dispatchEvent(new EventConstructor('cart_item_count_change', { detail: count }));
}

export function clearCartCount(doc = document, EventConstructor = CustomEvent) {
  updateCartCount({ activeItemTotalQuantity: 0 }, doc, EventConstructor);
}

export function fetchCartRecos({ activeItems }, fetchRecosData = fetchProductRecos) {
  const randomIndex = Math.floor(Math.random() * activeItems.length);
  const product = activeItems[randomIndex];

  if (product) {
    const { productId, styleId } = product;
    return fetchRecosData({ productId, styleId, slot: 'zap_cart', limit: CART_RECO_COUNT });
  } else {
    return { type: 'NOOP' };
  }
}

export function fetchCartPromos(fetchZCSInfo = getLandingPageInfo) {
  return function (dispatch, getState) {
    const { cookies, client: { request }, environmentConfig: { api: { mafia } } } = getState();
    return fetchZCSInfo(mafia, 'zapposcart', cookies, request)
      .then(fetchErrorMiddleware)
      .then(response => {
        dispatch(receiveCartPromos(response));
      });
  };
}

export function fetchCartItems({ firePixel = false, shouldFetchRecos = false } = {}, apiCartData = cartData, fetchRecosData = fetchProductRecos) {
  return function (dispatch, getState) {
    const state = getState();
    const { cookies, environmentConfig: { api: { mafia } } } = state;
    const isAssignedInRewardsTransparency = isAssigned(HYDRA_RET, 1, state);
    const querystring = isAssignedInRewardsTransparency ? '?displayRewards=true' : '';
    return apiCartData(mafia, querystring, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchErrorMiddleware)
      .then(response => {
        if (ExecutionEnvironment.canUseDOM) {
          updateCartCount(response);
        }
        dispatch(receiveCartItems(response));

        const { cartRewards, cartRewards: { enrolled } = {} } = response || {};
        if (cartRewards) {
          dispatch(receiveCartRewards(cartRewards, enrolled));
        }

        if (firePixel) {
          dispatch(fireCartPixelServer(response));
        }

        if (shouldFetchRecos) {
          dispatch(fetchCartRecos(response, fetchRecosData));
        }

        return response;
      })
      .catch(e => {
        dispatch(showCartModal(false));
        return dispatch(setError(err.GENERIC,e));
      });
  };
}

export function navigateToCheckout({ forceBrowserPush, forceRefreshPush }) {
  return function(dispatch) {
    dispatch(triggerAssignment(placeboHydraTest));
    dispatch(navigateToCheckoutEvent());

    const pathname = `${checkoutUrl}/initiate`;

    dispatch(checkCustomerAuthentication(pathname, () => {
      const hydraCheckoutHardRoute = dispatch(triggerAssignment(HYDRA_6PM_HARDROUTE));
      const shouldForceRefresh = isInAssignment(hydraCheckoutHardRoute);
      shouldForceRefresh ? forceRefreshPush(pathname) : forceBrowserPush(pathname);
    }, () => trackEvent('TE_CHECKOUT_ERROR_NOT_AUTHORIZED'))); // TODO move the trackEvent into events/cart once https://github01.zappos.net/mweb/marty/issues/8228 is implemented, undo changes to checkCustomerAuthentication
  };
}

export function changeQuantity(data, { firePixel = false } = {}, apiChangeCart = changeCart) {
  return function (dispatch, getState) {
    const state = getState();
    const { cookies, environmentConfig: { api: { mafia } } } = state;
    const isAssignedInRewardsTransparency = isAssigned(HYDRA_RET, 1, state);
    const querystring = isAssignedInRewardsTransparency ? '?displayRewards=true' : '';
    dispatch(requestCartUpdate());

    return apiChangeCart(mafia, querystring, data, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchErrorMiddlewareAllowedErrors([400]))
      .then(response => {
        // we encountered a problem, return the problem to the caller to allow them to handle it
        // successful responses don't include the statusCode field at all
        if (!response.statusCode) {
          if (ExecutionEnvironment.canUseDOM) {
            updateCartCount(response);
          }
          dispatch(receiveCartItems(response));
          dispatch(cartUpdatedEvent());
          const { cartRewards, cartRewards: { enrolled } = {} } = response || {};
          if (cartRewards) {
            dispatch(receiveCartRewards(cartRewards, enrolled));
          }
          if (firePixel) {
            dispatch(fireCartPixelServer(response));
          }
        } else {
          dispatch(cartChangeFailure(response));
        }
        return response;
      })
      .catch(e => dispatch(setError(err.CART,e)));
  };
}

export function fetchPin(apiGetPin = getPin) {
  return function (dispatch, getState) {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    return apiGetPin(mafia, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchErrorMiddleware)
      .then(response => {
        dispatch(receivePin(response.pin));
      })
      .catch(() => {
        window.location.reload();
      });
  };
}

export function validateUserPin(pin, apiValidatePin = validatePin) {
  return function (dispatch, getState) {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    return apiValidatePin(mafia, pin, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchErrorMiddleware)
      .then(response => {
        dispatch(pinIsValid(response.isWhitelisted));
      })
      .catch(() => {
        window.location.reload();
      });
  };
}

export function getTrackingNumber(data, cb, apiGetTrackingLabel = getTrackingLabel) {
  return function (dispatch, getState) {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    return apiGetTrackingLabel(mafia, data, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchErrorMiddleware)
      .then(({ trackingNumber }) => {
        if (cb) {
          cb();
        }

        dispatch(receiveTrackingNumber(trackingNumber));
      })
      .catch(() => {
        window.location.reload();
      });
  };
}
